import { RefObject, useEffect, useRef } from 'react';
import {
  LISTENER_OPTIONS,
  OPEN_TRIGGER_SELECTOR,
  SCROLL_VIEWPORT_SELECTOR,
} from '../../../../constants';

import { getColumnFilterClipPath } from '../utils/getColumnFilterClipPath';
import { getScrollViewportBounds } from '../utils/getScrollViewportBounds';

// PrimeReact portals the column filter overlay to document.body, so nothing clips it to the grid.
export const useColumnFilterOverlayClip = (
  gridRef: RefObject<HTMLElement | null>
): void => {
  // Pending animation frame handle: mutated from listeners, must not re-render.
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const container: HTMLElement | null = gridRef.current;
    if (!container) return;

    // Runs one frame later, once PrimeReact has re-aligned the overlay.
    const applyClip = (): void => {
      frameRef.current = null;

      const trigger: HTMLElement | null = container.querySelector(
        OPEN_TRIGGER_SELECTOR
      );
      const overlayId: string | null =
        trigger?.getAttribute('aria-controls') ?? null;
      const overlay: HTMLElement | null = overlayId
        ? document.getElementById(overlayId)
        : null;
      // Measure against the viewport holding the trigger; a nested slave grid has its own.
      const viewport: Element | null =
        trigger?.closest(SCROLL_VIEWPORT_SELECTOR) ?? null;
      if (!trigger || !overlay || !viewport) return;

      overlay.style.clipPath = getColumnFilterClipPath(
        overlay.getBoundingClientRect(),
        trigger.getBoundingClientRect(),
        getScrollViewportBounds(viewport)
      );
    };

    const scheduleClip = (): void => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(applyClip);
    };

    container.addEventListener('scroll', scheduleClip, LISTENER_OPTIONS);
    // A filter can also be opened on a half-scrolled-out button.
    container.addEventListener('click', scheduleClip, LISTENER_OPTIONS);
    // PrimeReact re-aligns the overlay on resize too.
    window.addEventListener('resize', scheduleClip);

    return () => {
      container.removeEventListener('scroll', scheduleClip, LISTENER_OPTIONS);
      container.removeEventListener('click', scheduleClip, LISTENER_OPTIONS);
      window.removeEventListener('resize', scheduleClip);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [gridRef]);
};
